import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  deleteToken,
} from "firebase/messaging";
import firebaseConfig from "../config/firebase";
import { post } from "../api/axios";
import endpoints from "../api/endpoints";

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig);
const messaging = getMessaging(firebaseApp);

// VAPID key for web push notifications (you need to generate this in Firebase Console)
const VAPID_KEY =
  "BM2_pU2RROyGVU8i4oBZ3GIhoDHmVD_3vixZic7Wz8VbKmK5Hm4xpko4Yv1AfhMfSpO8cc3GTDT2vQxkFPNdf_Y"; // Replace with your actual VAPID key from Firebase Console

/**
 * Request notification permission from user
 * @returns {Promise<NotificationPermission>} Permission status
 */
export const requestNotificationPermission = async () => {
  try {
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return "denied";
    }

    const permission = await Notification.requestPermission();
    return permission;
  } catch (error) {
    console.error("Error requesting notification permission:", error);
    return "denied";
  }
};

/**
 * Get FCM token for push notifications
 * @param {boolean} forceRefresh - Force refresh the token
 * @returns {Promise<string|null>} FCM token or null if failed
 */
export const getFCMToken = async (forceRefresh = false) => {
  try {
    const permission = await Notification.requestPermission();

    if (permission !== "granted") {
      console.warn("Notification permission not granted");
      return null;
    }

    // Get existing service worker registration or register a new one
    let registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      registration = await navigator.serviceWorker.register(
        "/firebase-messaging-sw.js",
      );
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });

    if (token) {
      // Store token in localStorage for later use
      localStorage.setItem("fcmToken", token);
      return token;
    } else {
      console.warn("No FCM token available");
      return null;
    }
  } catch (error) {
    console.error("Error getting FCM token:", error);
    return null;
  }
};

/**
 * Delete FCM token (useful for logout)
 * @returns {Promise<boolean>} Success status
 */
export const deleteFCMToken = async () => {
  try {
    const token = localStorage.getItem("fcmToken");
    if (token) {
      await deleteToken(messaging);
      localStorage.removeItem("fcmToken");
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error deleting FCM token:", error);
    return false;
  }
};

/**
 * Listen for foreground messages
 * @param {Function} callback - Callback function to handle incoming messages
 * @returns {Function} Unsubscribe function
 */
export const onForegroundMessage = (callback) => {
  return onMessage(messaging, (payload) => {
    callback(payload);
  });
};

/**
 * Display notification in foreground
 * @param {Object} payload - FCM message payload
 * @param {string} userType - 'customer' or 'restaurant'
 */
export const displayForegroundNotification = (
  payload,
  userType = "customer",
) => {
  const notificationTitle = payload.notification?.title || "Omaha Food Lovers";
  const notificationOptions = {
    body: payload.notification?.body || "",
    icon: payload.notification?.icon || "/logo192.png",
    badge: payload.notification?.badge || "/logo192.png",
    data: {
      ...payload.data,
      userType,
      clickUrl: getNotificationRedirectUrl(payload.data, userType),
    },
    tag: payload.data?.tag || "default",
    requireInteraction: payload.data?.requireInteraction || false,
  };

  // Show notification
  if ("Notification" in window && Notification.permission === "granted") {
    const notification = new Notification(
      notificationTitle,
      notificationOptions,
    );

    // Handle notification click
    notification.onclick = (event) => {
      event.preventDefault();
      handleNotificationClick(payload.data, userType);
    };
  }
};

/**
 * Get redirect URL based on notification data and user type
 * @param {Object} data - Notification data
 * @param {string} userType - 'customer' or 'restaurant'
 * @returns {string} Redirect URL
 */
export const getNotificationRedirectUrl = (data, userType = "customer") => {
  // If notification has a specific redirect URL, use it
  const notificationUrl = data?.url || data?.redirect_url || null;
  if (notificationUrl) {
    return notificationUrl;
  }

  // Default redirects based on user type
  if (userType === "restaurant") {
    // Restaurant portal redirects
    switch (data?.type) {
      case "wallet":
        return "/restaurant/wallet";
      case "settings":
        return "/restaurant/settings";
      case "dashboard":
        return "/restaurant/dashboard";
      default:
        return "/restaurant/dashboard";
    }
  } else {
    // Customer portal redirects
    switch (data?.type) {
      case "wallet":
        return "/wallet";
      case "vouchers":
        return "/vouchers";
      case "profile":
        return "/profile";
      default:
        return "/dashboard";
    }
  }
};

/**
 * Handle notification click with appropriate redirection
 * @param {Object} data - Notification data
 * @param {string} userType - 'customer' or 'restaurant'
 */
export const handleNotificationClick = (data, userType = "customer") => {
  const redirectUrl = getNotificationRedirectUrl(data, userType);

  // Focus on the window/tab if it's already open
  if (window.focus) {
    window.focus();
  }

  // Navigate to the appropriate page
  if (redirectUrl) {
    window.location.href = redirectUrl;
  }
};

/**
 * Register FCM token with backend API
 * @param {string} token - FCM token
 * @param {string} userType - 'customer' or 'restaurant'
 * @returns {Promise<boolean>} Success status
 */
export const registerTokenWithBackend = async (
  token,
  userType = "customer",
) => {
  try {
    // Calculate expiration date (1 day from now)
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 1);

    await post(endpoints.fcmToken, {
      fcm_token: token,
      fcm_token_expires_at: expiresAt.toISOString(),
    });
    return true;
  } catch (error) {
    console.error(
      `Error registering FCM token with ${userType} backend:`,
      error,
    );
    return false;
  }
};

/**
 * Initialize Firebase messaging
 * @param {Function} onMessageCallback - Callback for foreground messages
 * @param {string} userType - 'customer' or 'restaurant'
 * @returns {Promise<{token: string|null, permission: string}>} Token and permission status
 */
export const initializeFirebaseMessaging = async (
  onMessageCallback = null,
  userType = "customer",
) => {
  try {
    // Request permission
    const permission = await requestNotificationPermission();

    if (permission !== "granted") {
      console.warn("Notification permission not granted");
      return { token: null, permission };
    }

    // Get FCM token
    const token = await getFCMToken();

    if (token) {
      // Register token with backend
      await registerTokenWithBackend(token, userType);
    }

    // Set up foreground message listener
    if (onMessageCallback) {
      const unsubscribe = onForegroundMessage((payload) => {
        displayForegroundNotification(payload, userType);
        onMessageCallback(payload);
      });

      // Set up background notification click handler
      setupBackgroundMessageHandler(userType);

      // Return unsubscribe function for cleanup
      return {
        token,
        permission,
        unsubscribe,
      };
    }

    return { token, permission };
  } catch (error) {
    console.error("Error initializing Firebase messaging:", error);
    return { token: null, permission: "denied" };
  }
};

/**
 * Setup background message click handler
 * @param {string} userType - 'customer' or 'restaurant'
 */
export const setupBackgroundMessageHandler = (userType = "customer") => {
  // Handle notification clicks when app is in background
  if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      if (event.data && event.data.type === "notification-click") {
        const notificationData = event.data.data;
        handleNotificationClick(notificationData, userType);
      }
    });
  }
};

/**
 * Get stored FCM token from localStorage
 * @returns {string|null} Stored token or null
 */
export const getStoredFCMToken = () => {
  return localStorage.getItem("fcmToken");
};

/**
 * Check if notifications are supported
 * @returns {boolean} Support status
 */
export const isNotificationSupported = () => {
  return (
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
};

export { messaging, firebaseApp };
